import { WordDetail, WordResponse } from '@/api/type'

/**
 * 音声認識の結果をアライメントするクラス
 * Needleman-Wunschアルゴリズム（動的計画法）を応用
 */

type AlignmentType = 'MATCH' | 'SUBSTITUTION' | 'DELETION' | 'INSERTION'

export type AlignmentResult = {
  type: AlignmentType
  ref: WordResponse | null
  hyp: WordDetail | null
}

class SpeechAligner {
  private readonly COSTS = {
    MATCH: 0, // 一致
    SUBSTITUTION: 10, // 言い間違い
    INSERTION: 10, // 余分な単語
    DELETION: 10, // 言わなかった
  }

  /**
   * 2つの単語の類似度コストを計算する
   */
  private calculateCost(ref: WordResponse, hyp: WordDetail): number {
    // 1. 完全一致ならコスト0
    if (ref.word.toLowerCase() === hyp.Word.toLowerCase()) {
      return this.COSTS.MATCH
    }

    // 2. 時間的な重なりをチェック (Time Overlap)
    const hypStart = hyp.Offset / 10_000_000 // 100ns単位から秒へ変換
    const hypEnd = (hyp.Offset + hyp.Duration) / 10_000_000

    const overlapStart = Math.max(ref.start, hypStart)
    const overlapEnd = Math.min(ref.end, hypEnd)
    const overlapDuration = Math.max(0, overlapEnd - overlapStart)

    const refDuration = ref.end - ref.start
    const hypDuration = hypEnd - hypStart
    const minDuration = Math.min(refDuration, hypDuration)

    // 時間が大きく重なっている（例: 50%以上）なら、言い間違いの可能性が高い -> コストを下げる
    const timeBonus = overlapDuration / minDuration > 0.5 ? -2 : 0

    // 3. 文字列の類似度（Levenshtein距離）
    const dist = this.levenshteinDistance(ref.word, hyp.Word)
    const length = Math.max(ref.word.length, hyp.Word.length)
    const similarityCost = (dist / length) * 10 // 正規化して重み付け

    return this.COSTS.SUBSTITUTION + similarityCost + timeBonus
  }

  /**
   * アライメント実行
   */
  align(referenceTokens: WordResponse[], hypothesisTokens: WordDetail[]): AlignmentResult[] {
    const n = referenceTokens.length
    const m = hypothesisTokens.length

    // DPテーブルの初期化
    const dp: number[][] = Array(n + 1)
      .fill(null)
      .map(() => Array(m + 1).fill(0))
    const ptr: (string | null)[][] = Array(n + 1)
      .fill(null)
      .map(() => Array(m + 1).fill(null))

    // 初期値設定
    for (let i = 0; i <= n; i++) dp[i][0] = i * this.COSTS.DELETION
    for (let j = 0; j <= m; j++) dp[0][j] = j * this.COSTS.INSERTION

    // DP計算
    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= m; j++) {
        const costSub = this.calculateCost(referenceTokens[i - 1], hypothesisTokens[j - 1])

        const matchOrSub = dp[i - 1][j - 1] + costSub
        const del = dp[i - 1][j] + this.COSTS.DELETION
        const ins = dp[i][j - 1] + this.COSTS.INSERTION

        if (matchOrSub <= del && matchOrSub <= ins) {
          dp[i][j] = matchOrSub
          ptr[i][j] = 'DIAG' // 斜め（一致 or 置換）
        } else if (del <= ins) {
          dp[i][j] = del
          ptr[i][j] = 'UP' // 上（削除）
        } else {
          dp[i][j] = ins
          ptr[i][j] = 'LEFT' // 左（挿入）
        }
      }
    }

    // バックトレース（経路復元）
    let i = n
    let j = m
    const alignment: AlignmentResult[] = []

    while (i > 0 || j > 0) {
      if (i > 0 && j > 0 && ptr[i][j] === 'DIAG') {
        const ref = referenceTokens[i - 1]
        const hyp = hypothesisTokens[j - 1]
        const type: AlignmentType = ref.word.toLowerCase() === hyp.Word.toLowerCase() ? 'MATCH' : 'SUBSTITUTION'

        alignment.unshift({ type, ref, hyp })
        i--
        j--
      } else if (i > 0 && (j === 0 || ptr[i][j] === 'UP')) {
        alignment.unshift({ type: 'DELETION', ref: referenceTokens[i - 1], hyp: null })
        i--
      } else {
        alignment.unshift({ type: 'INSERTION', ref: null, hyp: hypothesisTokens[j - 1] })
        j--
      }
    }

    return alignment
  }

  /**
   * レーベンシュタイン距離
   */
  private levenshteinDistance(s1: string, s2: string): number {
    const a = s1.toLowerCase()
    const b = s2.toLowerCase()
    const matrix: number[][] = []

    for (let i = 0; i <= b.length; i++) matrix[i] = [i]
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1)
        }
      }
    }
    return matrix[b.length][a.length]
  }
}

export const speechAligner = new SpeechAligner()
